// File: frontend/src/pages/statistics/StatisticsPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./StatisticsPage.css";
import { resolveClubName } from "../../services/matchService";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { parseServerDate } from "../../utils/dateUtils";
import {
  AuthorOverview,
  DistributionItem,
  FanClubStatItem,
  GeneralStats,
  StatPeriod,
  TopAuthor,
  TopClub,
  TopEntry,
  TopKarmaUser,
  TopPerson,
  TopTeam,
  statisticsService,
} from "../../services/statisticsService";

// Prepend API base to root-relative logo paths (e.g. "/logos/team_549.png")
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const logoSrc = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url}`;
};

// ─── Types ────────────────────────────────────────────────────────────────────

type MainTab     = "general" | "authors" | "rookies" | "entries" | "people" | "clubs" | "teams" | "fans" | "leaderboard";

// Sub-tabs inside the Liderlik Tablosu main tab
type LeaderboardSubTab = "topEntries" | "topKarma";
type RoleSubTab  = "demographics" | "entries";

// ─── Constants ────────────────────────────────────────────────────────────────

const PERIODS: { key: StatPeriod; label: string }[] = [
  { key: "today", label: "Bugün" },
  { key: "week",  label: "Bu Hafta" },
  { key: "month", label: "Bu Ay" },
  { key: "year",  label: "Bu Yıl" },
  { key: "all",   label: "Tüm Zamanlar" },
];

const GENDER_LABELS: Record<string, string> = {
  MALE:        "Erkek",
  FEMALE:      "Kadın",
  OTHER:       "Diğer",
  UNSPECIFIED: "Belirtilmemiş",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatLocalDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  const d = parseServerDate(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};

// ─── Period selector ──────────────────────────────────────────────────────────

const PeriodSelector: React.FC<{ value: StatPeriod; onChange: (p: StatPeriod) => void }> = ({ value, onChange }) => (
  <div className="stats-period-row">
    {PERIODS.map((p) => (
      <button
        key={p.key}
        className={`stats-period-btn${value === p.key ? " stats-period-btn--active" : ""}`}
        onClick={() => onChange(p.key)}
      >
        {p.label}
      </button>
    ))}
  </div>
);

// ─── Bar chart ────────────────────────────────────────────────────────────────

const BarChart: React.FC<{ items: DistributionItem[]; title: string; subtitle?: string; wide?: boolean }> = ({
  items, title, subtitle, wide,
}) => {
  // Always collapsed by default — user clicks to expand
  const [open, setOpen] = useState<boolean>(false);

  const total = items ? items.reduce((s, i) => s + i.count, 0) : 0;
  const max   = items && items.length > 0 ? Math.max(...items.map((i) => i.count)) : 0;

  return (
    <div className={`stats-chart-card${wide ? " stats-chart-card--wide" : ""}`}>
      {/* Clickable header toggles the list */}
      <div className="stats-chart-card__header" onClick={() => setOpen((o) => !o)}>
        <div>
          <p className="stats-chart-card__title" style={{ margin: 0 }}>{title}</p>
          {subtitle && <p className="stats-chart-card__subtitle" style={{ margin: "2px 0 0" }}>{subtitle}</p>}
        </div>
        <button className="stats-chart-card__toggle">
          {open
            ? <span className="stats-chart-card__toggle-close">▲ Kapat</span>
            : <span className="stats-chart-card__toggle-open">▼ Göster ({items?.length ?? 0})</span>}
        </button>
      </div>

      {open && (
        <div className="stats-bar-list" style={{ marginTop: 12 }}>
          {(!items || items.length === 0) ? (
            <div className="stats-empty" style={{ padding: "12px 0" }}>Veri yok.</div>
          ) : items.map((item) => {
            const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0.0";
            return (
              <div className="stats-bar-item" key={item.label}>
                <span className="stats-bar-item__label">{item.label}</span>
                <div className="stats-bar-item__track">
                  <div className="stats-bar-item__fill" style={{ width: max > 0 ? `${(item.count / max) * 100}%` : "0%" }} />
                </div>
                <span className="stats-bar-item__count">
                  {item.count.toLocaleString("tr-TR")}
                  <span className="stats-bar-item__pct"> %{pct}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Split city chart (Yurtiçi + Yurtdışı) ───────────────────────────────────

// Number of abroad city rows visible before "Diğerleri" expansion
const ABROAD_VISIBLE_COUNT = 5;

const SplitCityChart: React.FC<{
  domesticItems: DistributionItem[];
  abroadItems: DistributionItem[];
  subtitle: string;
}> = ({ domesticItems, abroadItems, subtitle }) => {
  const [open, setOpen]               = useState(false);
  const [abroadExpanded, setAbroadExpanded] = useState(false);

  const renderBars = (items: DistributionItem[]) => {
    const total = items.reduce((s, i) => s + i.count, 0);
    const max   = items.length > 0 ? Math.max(...items.map((i) => i.count)) : 0;
    return items.map((item) => {
      const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0.0";
      return (
        <div className="stats-bar-item" key={item.label}>
          <span className="stats-bar-item__label">{item.label}</span>
          <div className="stats-bar-item__track">
            <div className="stats-bar-item__fill" style={{ width: max > 0 ? `${(item.count / max) * 100}%` : "0%" }} />
          </div>
          <span className="stats-bar-item__count">
            {item.count.toLocaleString("tr-TR")}
            <span className="stats-bar-item__pct"> %{pct}</span>
          </span>
        </div>
      );
    });
  };

  const totalCount = domesticItems.length + abroadItems.length;
  const abroadVisible   = abroadItems.slice(0, ABROAD_VISIBLE_COUNT);
  const abroadHidden    = abroadItems.slice(ABROAD_VISIBLE_COUNT);
  const hasHiddenAbroad = abroadHidden.length > 0;

  return (
    <div className="stats-chart-card stats-chart-card--wide">
      <div className="stats-chart-card__header" onClick={() => setOpen((o) => !o)}>
        <div>
          <p className="stats-chart-card__title" style={{ margin: 0 }}>Şehir Dağılımı</p>
          <p className="stats-chart-card__subtitle" style={{ margin: "2px 0 0" }}>{subtitle}</p>
        </div>
        <button className="stats-chart-card__toggle">
          {open
            ? <span className="stats-chart-card__toggle-close">▲ Kapat</span>
            : <span className="stats-chart-card__toggle-open">▼ Göster ({totalCount})</span>}
        </button>
      </div>

      {open && (
        <div className="stats-bar-list" style={{ marginTop: 12 }}>
          {/* Domestic section */}
          <div className="stats-city-section-label">🇹🇷 Yurtiçi</div>
          {domesticItems.length === 0
            ? <div className="stats-empty" style={{ padding: "8px 0 12px" }}>Veri yok.</div>
            : renderBars(domesticItems)
          }

          {/* Abroad section */}
          <div className="stats-city-section-label" style={{ marginTop: 16 }}>✈️ Yurtdışı</div>
          {abroadItems.length === 0 ? (
            <div className="stats-empty" style={{ padding: "8px 0" }}>Veri yok.</div>
          ) : (
            <>
              {renderBars(abroadVisible)}
              {/* Hidden rows — only shown when user explicitly expands */}
              {abroadExpanded && renderBars(abroadHidden)}
              {hasHiddenAbroad && (
                <button
                  className="stats-abroad-toggle-btn"
                  onClick={(e) => { e.stopPropagation(); setAbroadExpanded((v) => !v); }}
                  type="button"
                >
                  {abroadExpanded
                    ? "▲ Daha az göster"
                    : `▼ Diğerleri (${abroadHidden.length} şehir daha)`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Demographics panel ───────────────────────────────────────────────────────

const DemographicsPanel: React.FC<{ role: "AUTHOR" | "ROOKIE"; roleLabel: string }> = ({ role, roleLabel }) => {
  const [overview, setOverview] = useState<AuthorOverview | null>(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    setLoading(true);
    setOverview(null);
    statisticsService
      .getAuthorOverview(role)
      .then(setOverview)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [role]);

  if (loading) return <div className="stats-loading">Yükleniyor…</div>;
  if (!overview) return <div className="stats-error">Veriler yüklenemedi.</div>;

  const subtitle = `Sadece aktif ${roleLabel.toLowerCase()}lara ait veriler`;

  return (
    <div className="stats-charts-grid">
      <BarChart
        title="Cinsiyet Dağılımı"
        subtitle={subtitle}
        items={overview.genderDistribution.map((d) => ({
          label: GENDER_LABELS[d.label] ?? d.label,
          count: d.count,
        }))}
      />
      <BarChart
        title="Yaş Dağılımı"
        subtitle={subtitle}
        items={overview.ageDistribution}
      />
      {/* Country distribution — placed above city, uses md_country display names */}
      <BarChart
        wide
        title="Ülke Dağılımı"
        subtitle={subtitle}
        items={overview.countryDistribution}
      />
      {/* City distribution split into domestic (full list) and abroad (top 5 + others) */}
      <SplitCityChart
        domesticItems={overview.domesticCityDistribution}
        abroadItems={overview.abroadCityDistribution}
        subtitle={`${subtitle} (yurtiçi ilk 50, yurtdışı ilk 5 gösterilir)`}
      />
    </div>
  );
};

// ─── Top authors panel (used inside Yazarlar / Çaylaklar role tabs) ───────────

const TopAuthorsPanel: React.FC<{ role: "AUTHOR" | "ROOKIE" }> = ({ role }) => {
  const [period, setPeriod]   = useState<StatPeriod>("all");
  const [list, setList]       = useState<TopAuthor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod) => {
    setLoading(true);
    setError("");
    statisticsService
      .getTopAuthors(p, role)
      .then(setList)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => { load(period); }, [period, load]);

  return (
    <div>
      <PeriodSelector value={period} onChange={setPeriod} />
      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}
      {!loading && !error && list.length === 0 && <div className="stats-empty">Bu dönem için veri yok.</div>}
      {!loading && list.length > 0 && (
        <div className="stats-rank-list">
          {list.map((author, idx) => (
            <div className="stats-rank-item" key={author.userId}>
              <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                {idx + 1}
              </span>
              <div className="stats-rank-item__main">
                <Link to={`/yazar/${author.username}--${author.userId}`} className="stats-rank-item__username">
                  {author.username}
                </Link>
              </div>
              <span className="stats-rank-item__count">
                {author.entryCount.toLocaleString("tr-TR")} tanım
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Role tab (Yazarlar / Çaylaklar) — with Demografi | Tanımlar sub-tabs ────

const RoleTab: React.FC<{ role: "AUTHOR" | "ROOKIE"; roleLabel: string }> = ({ role, roleLabel }) => {
  const [subTab, setSubTab] = useState<RoleSubTab>("demographics");

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="stats-subtabs">
        <button
          className={`stats-subtab${subTab === "demographics" ? " stats-subtab--active" : ""}`}
          onClick={() => setSubTab("demographics")}
        >
          Demografi
        </button>
        <button
          className={`stats-subtab${subTab === "entries" ? " stats-subtab--active" : ""}`}
          onClick={() => setSubTab("entries")}
        >
          Tanımlar
        </button>
      </div>

      {subTab === "demographics" && (
        <DemographicsPanel key={`demo-${role}`} role={role} roleLabel={roleLabel} />
      )}
      {subTab === "entries" && (
        <TopAuthorsPanel key={`top-${role}`} role={role} />
      )}
    </div>
  );
};

// ─── General tab ─────────────────────────────────────────────────────────────

const GeneralTab: React.FC = () => {
  const [data, setData]       = useState<GeneralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    statisticsService.getGeneralStats()
      .then(setData)
      .catch(() => setError("İstatistikler yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="stats-loading">Yükleniyor…</div>;
  if (error)   return <div className="stats-error">{error}</div>;
  if (!data)   return null;

  const fmt = (n: number) => n.toLocaleString("tr-TR");

  // Single-value general stats.
  const singleStats: [string, string][] = [
    ["Toplam Aktif Tanım",        fmt(data.totalEntries)],
    ["Toplam Silinmiş Tanım",     fmt(data.totalDeletedEntries)],
    // Average number of characters per active entry, across all five domains.
    ["Ortalama Aktif Tanım Uzunluğu", `${data.avgEntryCharCount.toFixed(2)} karakter`],
    ["Aktif Yazar Sayısı",        fmt(data.totalActiveAuthors)],
    ["Aktif Çaylak Sayısı",      fmt(data.totalActiveNewbies)],
    ["Verilen Artı Oy",           fmt(data.totalUpvotes)],
    ["Konu Başına Düşen Tanım",  data.avgEntriesPerTopic.toFixed(2)],
    ["Yazar Başına Düşen Tanım", data.avgEntriesPerAuthor.toFixed(2)],
  ];

  // Per-domain breakdown: total topics (konu), active entries (tanım), and
  // deleted entries (silinmiş tanım) per domain.
  // Order requested: gündem (custom topic), maç, kulüp, kişi, takım.
  // Tuple: [label, totalTopics, activeEntries, deletedEntries]
  const domainStats: [string, number, number, number][] = [
    ["Gündem", data.totalCustomTopics, data.totalCustomTopicEntries, data.totalCustomTopicEntriesDeleted],
    ["Maç",    data.totalMatches,      data.totalMatchEntries,       data.totalMatchEntriesDeleted],
    ["Kulüp",  data.totalClubs,        data.totalClubEntries,        data.totalClubEntriesDeleted],
    ["Kişi",   data.totalPeople,       data.totalPersonEntries,      data.totalPersonEntriesDeleted],
    ["Takım",  data.totalTeams,        data.totalTeamEntries,        data.totalTeamEntriesDeleted],
  ];

  return (
    <div className="stats-general-groups">
      <div className="stats-table-section">
        <p className="stats-table-section__title">Genel İstatistikler</p>
        <table className="stats-table">
          <tbody>
            {singleStats.map(([label, val]) => (
              <tr key={label}>
                <td className="stats-table__label">{label}</td>
                <td className="stats-table__value">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="stats-table-section">
        <p className="stats-table-section__title">Konu / Tanım Dağılımı</p>
        <table className="stats-table">
          <thead>
            <tr>
              <th className="stats-table__label stats-table__col-head">Alan</th>
              <th className="stats-table__value stats-table__col-head">Toplam Konu</th>
              <th className="stats-table__value stats-table__col-head">Aktif Tanım</th>
              <th className="stats-table__value stats-table__col-head">Silinmiş Tanım</th>
            </tr>
          </thead>
          <tbody>
            {domainStats.map(([label, topics, activeEntries, deletedEntries]) => (
              <tr key={label}>
                <td className="stats-table__label">{label}</td>
                <td className="stats-table__value">{fmt(topics)}</td>
                <td className="stats-table__value">{fmt(activeEntries)}</td>
                <td className="stats-table__value">{fmt(deletedEntries)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Entries tab ──────────────────────────────────────────────────────────────

const EntriesTab: React.FC = () => {
  const [period, setPeriod] = useState<StatPeriod>("all");
  const [sort, setSort]     = useState<"votes" | "favorites">("votes");

  // Entry list for votes/favorites sort modes
  const [entryList, setEntryList] = useState<TopEntry[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod, s: "votes" | "favorites") => {
    setLoading(true);
    setError("");
    statisticsService.getTopEntries(p, s)
      .then(setEntryList)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period, sort); }, [period, sort, load]);

  const entryLink = (e: TopEntry) => {
    if (e.matchSlug)  return `/${e.matchSlug}#entry-${e.entryId}`;
    // Fallback for matches whose permanent slug column is NULL (e.g. fixtures inserted
    // via seeding SQL) — rebuild the URL from live data; routing only relies on the
    // trailing --{id} segment, so the regenerated slug always resolves correctly.
    if (e.matchId && e.homeTeamName && e.awayTeamName && e.matchDate) {
      const url = generateMatchUrlFromEntry(
        e.homeTeamName,
        e.awayTeamName,
        new Date(e.matchDate + "T00:00:00"),
        e.matchId
      );
      return `${url}#entry-${e.entryId}`;
    }
    if (e.personSlug) return `/kisi/${e.personSlug}#entry-${e.entryId}`;
    if (e.clubSlug)   return `/kulup/${e.clubSlug}#entry-${e.entryId}`;
    if (e.teamSlug && e.teamClubSlug) return `/takim/${e.teamClubSlug}/${e.teamSlug}#entry-${e.entryId}`;
    if (e.customTopicSlug) return `/gundem/${e.customTopicSlug}#entry-${e.entryId}`;
    return "#";
  };

  const entrySubject = (e: TopEntry) => {
    if (e.homeTeamName && e.awayTeamName) return `${e.homeTeamName} - ${e.awayTeamName}`;
    if (e.personName)  return e.personName;
    if (e.clubName)    return e.clubName;
    if (e.teamName)    return e.teamName;
    if (e.customTopicTitle) return e.customTopicTitle;
    if (e.matchSlug)   return e.matchSlug;
    return "—";
  };

  const dateLabel = (e: TopEntry) =>
    e.matchDate ? formatLocalDate(e.matchDate) : formatDateTime(e.createdAt);

  return (
    <div>
      <div className="stats-sort-row">
        <button
          className={`stats-period-btn${sort === "votes" ? " stats-period-btn--active" : ""}`}
          onClick={() => setSort("votes")}
        >
          En Beğenilenler
        </button>
        <button
          className={`stats-period-btn${sort === "favorites" ? " stats-period-btn--active" : ""}`}
          onClick={() => setSort("favorites")}
        >
          En Favorilenenler
        </button>
      </div>

      <PeriodSelector value={period} onChange={setPeriod} />

      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}

      {/* Votes / favorites ranking — individual top entries */}
      {!loading && (
        <>
          {!error && entryList.length === 0 && <div className="stats-empty">Bu dönem için veri yok.</div>}
          {entryList.length > 0 && (
            <div className="stats-rank-list">
              {entryList.map((entry, idx) => (
                <div className="stats-rank-item" key={entry.entryId}>
                  <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                    {idx + 1}
                  </span>
                  <div className="stats-rank-item__main">
                    <Link to={`/yazar/${entry.authorUsername}--${entry.authorUserId}`} className="stats-rank-item__username">
                      {entry.authorUsername}
                    </Link>
                    <div className="stats-rank-item__meta">
                      <Link to={entryLink(entry)} className="stats-rank-item__subject-link">
                        {entrySubject(entry)}
                      </Link>
                      {" · "}{dateLabel(entry)}
                    </div>
                    <div className="stats-rank-item__preview">{entry.contentPreview}</div>
                  </div>
                  <span className="stats-rank-item__count">
                    {sort === "votes"
                      ? `+${entry.upvoteCount.toLocaleString("tr-TR")}`
                      : `★ ${entry.favoriteCount.toLocaleString("tr-TR")}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── People tab ───────────────────────────────────────────────────────────────

const PeopleTab: React.FC = () => {
  const [period, setPeriod]   = useState<StatPeriod>("all");
  const [list, setList]       = useState<TopPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod) => {
    setLoading(true);
    setError("");
    statisticsService
      .getTopPeople(p)
      .then(setList)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  return (
    <div>
      <PeriodSelector value={period} onChange={setPeriod} />

      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}
      {!loading && !error && list.length === 0 && (
        <div className="stats-empty">Bu dönem için veri yok.</div>
      )}

      {!loading && list.length > 0 && (
        <div className="stats-rank-list">
          {list.map((person, idx) => (
            <div className="stats-rank-item" key={person.personId}>
              <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                {idx + 1}
              </span>
              <div className="stats-rank-item__main">
                {/* Link to the person's page */}
                <Link to={`/kisi/${person.slug}`} className="stats-rank-item__username">
                  {person.displayName}
                </Link>
              </div>
              <span className="stats-rank-item__count">
                {person.entryCount.toLocaleString("tr-TR")} tanım
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Clubs tab ────────────────────────────────────────────────────────────────

const ClubsTab: React.FC = () => {
  const [period, setPeriod]   = useState<StatPeriod>("all");
  const [list, setList]       = useState<TopClub[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod) => {
    setLoading(true);
    setError("");
    statisticsService
      .getTopClubs(p)
      .then(setList)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  return (
    <div>
      <PeriodSelector value={period} onChange={setPeriod} />

      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}
      {!loading && !error && list.length === 0 && (
        <div className="stats-empty">Bu dönem için veri yok.</div>
      )}

      {!loading && list.length > 0 && (
        <div className="stats-rank-list">
          {list.map((club, idx) => {
            const src = logoSrc(club.logoUrl);
            return (
              <div className="stats-rank-item" key={club.clubId}>
                <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                  {idx + 1}
                </span>
                <div className="stats-rank-item__avatar">
                  {src
                    ? <img src={src} alt={club.shortName} />
                    : <span>{(club.shortName || "?").slice(0, 2).toUpperCase()}</span>
                  }
                </div>
                <div className="stats-rank-item__main">
                  <Link to={`/kulup/${club.slug}`} className="stats-rank-item__username">
                    {resolveClubName(club)}
                  </Link>
                </div>
                <span className="stats-rank-item__count">
                  {club.entryCount.toLocaleString("tr-TR")} tanım
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Teams tab ────────────────────────────────────────────────────────────────

const TeamsTab: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod]   = useState<StatPeriod>("all");
  const [list, setList]       = useState<TopTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod) => {
    setLoading(true);
    setError("");
    statisticsService
      .getTopTeams(p)
      .then(setList)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  return (
    <div>
      <PeriodSelector value={period} onChange={setPeriod} />

      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}
      {!loading && !error && list.length === 0 && (
        <div className="stats-empty">Bu dönem için veri yok.</div>
      )}

      {!loading && list.length > 0 && (
        <div className="stats-rank-list">
          {list.map((team, idx) => {
            const src = logoSrc(team.logoUrl);
            return (
              <div className="stats-rank-item" key={team.teamId}>
                <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                  {idx + 1}
                </span>
                <div className="stats-rank-item__avatar">
                  {src
                    ? <img src={src} alt={team.displayName} />
                    : <span>{(team.displayName || "?").slice(0, 2).toUpperCase()}</span>
                  }
                </div>
                <div className="stats-rank-item__main">
                  <Link to={`/kulup/${team.clubSlug}`} className="stats-rank-item__username">
                    {team.displayName}
                  </Link>
                  {team.sportType && (
                    <span className="stats-rank-item__meta" style={{ marginLeft: 6, fontSize: "0.8em", opacity: 0.65 }}>
                      {/* Raw enum values (FOOTBALL, ICE_HOCKEY, ...) map 1:1 onto the
                          menu_sport_* keys already used by the left-pane sport filter,
                          so the label follows the interface language. The raw value is
                          kept as the fallback for a sport that has no key yet. */}
                      {t(`menu_sport_${team.sportType.toLowerCase()}`, team.sportType.toLowerCase())}
                    </span>
                  )}
                </div>
                <span className="stats-rank-item__count">
                  {team.entryCount.toLocaleString("tr-TR")} tanım
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Fan club statistics tab ──────────────────────────────────────────────────

const ORDER_LABELS: Record<number, string> = {
  1: "1. Takım (Ana)",
  2: "2. Takım",
  3: "3. Takım",
  4: "4. Takım",
  5: "5. Takım",
};

// How many rows to show before the "show more" button appears
const FAN_PANEL_DEFAULT_VISIBLE = 10;

const FanOrderPanel: React.FC<{ order: number; role: "AUTHOR" | "ROOKIE" }> = ({ order, role }) => {
  const [items, setItems]       = useState<FanClubStatItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    setExpanded(false);
    statisticsService.getFanStats(order, role)
      .then(setItems)
      .catch(() => setError("Liste yüklenemedi."))
      .finally(() => setLoading(false));
  }, [order, role]);

  const total   = items.reduce((s, i) => s + i.count, 0);
  const max     = items.length > 0 ? items[0].count : 0;
  const visible = expanded ? items : items.slice(0, FAN_PANEL_DEFAULT_VISIBLE);
  const hidden  = items.length - FAN_PANEL_DEFAULT_VISIBLE;

  return (
    <div className="stats-fan-order-panel">
      <h4 className="stats-fan-order-panel__title">
        {ORDER_LABELS[order]}
        <span className="stats-fan-order-panel__total"> · {items.length} kulüp</span>
      </h4>
      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="stats-empty">Bu sıra için veri yok.</div>
      )}
      {!loading && items.length > 0 && (
        <>
          <div className="stats-fan-list">
            {visible.map((item, idx) => {
              const pct         = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0.0";
              const barColor    = item.color1 ?? "#aaa";
              const displayName = item.nameTr ?? item.nameEn ?? item.shortName;
              const src         = logoSrc(item.logoUrl);
              return (
                <div className="stats-fan-item" key={item.clubId}>
                  {/* Rank number */}
                  <span className={`stats-fan-item__pos${idx < 3 ? " stats-fan-item__pos--top3" : ""}`}>
                    {idx + 1}
                  </span>

                  {/* Logo with initials fallback underneath.
                      If the image fails to load (broken path), the hidden initials span becomes visible. */}
                  <div className="stats-fan-item__logo-wrap">
                    {src && (
                      <img
                        src={src}
                        alt={item.shortName}
                        className="stats-fan-item__logo"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                          const sib = (e.target as HTMLElement).nextElementSibling as HTMLElement | null;
                          if (sib) sib.style.display = "inline-flex";
                        }}
                      />
                    )}
                    <span
                      className="stats-fan-item__initials"
                      style={{ display: src ? "none" : "inline-flex" }}
                    >
                      {displayName.slice(0, 2).toUpperCase()}
                    </span>
                  </div>

                  {/* Name + bar stacked */}
                  <div className="stats-fan-item__main">
                    <span className="stats-fan-item__name">{displayName}</span>
                    <div className="stats-fan-item__bar-row">
                      <div className="stats-fan-item__track">
                        <div
                          className="stats-fan-item__fill"
                          style={{
                            width: max > 0 ? `${(item.count / max) * 100}%` : "0%",
                            backgroundColor: barColor,
                          }}
                        />
                      </div>
                      <span className="stats-fan-item__count">
                        {item.count.toLocaleString("tr-TR")}
                        <span className="stats-fan-item__pct"> %{pct}</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show more / collapse button */}
          {items.length > FAN_PANEL_DEFAULT_VISIBLE && (
            <button
              className="stats-fan-show-more"
              onClick={() => setExpanded((v) => !v)}
              type="button"
            >
              {expanded
                ? "▲ Daha az göster"
                : `▼ ${hidden} kulüp daha göster`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const FansTab: React.FC = () => {
  const [role, setRole] = useState<"AUTHOR" | "ROOKIE">("AUTHOR");

  return (
    <div>
      {/* Role selector */}
      <div className="stats-period-row">
        <button
          className={`stats-period-btn${role === "AUTHOR" ? " stats-period-btn--active" : ""}`}
          onClick={() => setRole("AUTHOR")}
        >
          Yazarlar
        </button>
        <button
          className={`stats-period-btn${role === "ROOKIE" ? " stats-period-btn--active" : ""}`}
          onClick={() => setRole("ROOKIE")}
        >
          Çaylaklar
        </button>
      </div>

      {/* One panel per order slot 1–5 */}
      <div className="stats-fan-grid">
        {[1, 2, 3, 4, 5].map((order) => (
          <FanOrderPanel key={`${role}-${order}`} order={order} role={role} />
        ))}
      </div>
    </div>
  );
};

// ─── Liderlik Tablosu tab — sub-tabs: En Çok Tanım | En Yüksek Karma ─────────

const LeaderboardTab: React.FC = () => {
  const [subTab, setSubTab] = useState<LeaderboardSubTab>("topEntries");
  const [period, setPeriod] = useState<StatPeriod>("all");

  // Entry count list — all users, all roles
  const [entryList, setEntryList] = useState<TopAuthor[]>([]);
  // Karma list — all users, all roles
  const [karmaList, setKarmaList] = useState<TopKarmaUser[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const load = useCallback((p: StatPeriod, s: LeaderboardSubTab) => {
    setLoading(true);
    setError("");
    if (s === "topEntries") {
      statisticsService
        .getTopUsersByEntryCount(p)
        .then(setEntryList)
        .catch(() => setError("Liste yüklenemedi."))
        .finally(() => setLoading(false));
    } else {
      statisticsService
        .getTopUsersByKarma(p)
        .then(setKarmaList)
        .catch(() => setError("Liste yüklenemedi."))
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => { load(period, subTab); }, [period, subTab, load]);

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="stats-subtabs">
        <button
          className={`stats-subtab${subTab === "topEntries" ? " stats-subtab--active" : ""}`}
          onClick={() => setSubTab("topEntries")}
        >
          En Çok Tanım
        </button>
        <button
          className={`stats-subtab${subTab === "topKarma" ? " stats-subtab--active" : ""}`}
          onClick={() => setSubTab("topKarma")}
        >
          En Yüksek Karma
        </button>
      </div>

      <PeriodSelector value={period} onChange={setPeriod} />

      {loading && <div className="stats-loading">Yükleniyor…</div>}
      {error   && <div className="stats-error">{error}</div>}

      {/* En Çok Tanım — all users ranked by total entry count (all topic types) */}
      {!loading && subTab === "topEntries" && (
        <>
          {entryList.length === 0
            ? <div className="stats-empty">Bu dönem için veri yok.</div>
            : (
              <div className="stats-rank-list">
                {entryList.map((user, idx) => (
                  <div className="stats-rank-item" key={user.userId}>
                    <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                      {idx + 1}
                    </span>
                    <div className="stats-rank-item__main">
                      <Link to={`/yazar/${user.username}--${user.userId}`} className="stats-rank-item__username">
                        {user.username}
                      </Link>
                    </div>
                    <span className="stats-rank-item__count">
                      {user.entryCount.toLocaleString("tr-TR")} tanım
                    </span>
                  </div>
                ))}
              </div>
            )
          }
        </>
      )}

      {/* En Yüksek Karma — all users ranked by karma (upVotes - downVotes) */}
      {!loading && subTab === "topKarma" && (
        <>
          {karmaList.length === 0
            ? <div className="stats-empty">Bu dönem için veri yok.</div>
            : (
              <div className="stats-rank-list">
                {karmaList.map((user, idx) => {
                  // Positive karma gets "+" prefix; zero shows plain "0"; negative already carries "-"
                  const karmaDisplay = user.karma > 0
                    ? `+${user.karma.toLocaleString("tr-TR")}`
                    : user.karma.toLocaleString("tr-TR");
                  return (
                    <div className="stats-rank-item" key={user.userId}>
                      <span className={`stats-rank-item__pos${idx < 3 ? " stats-rank-item__pos--top3" : ""}`}>
                        {idx + 1}
                      </span>
                      <div className="stats-rank-item__main">
                        <Link to={`/yazar/${user.username}--${user.userId}`} className="stats-rank-item__username">
                          {user.username}
                        </Link>
                        {/* Entry count shown as secondary info next to the username */}
                        <span className="stats-rank-item__meta" style={{ marginLeft: 8, fontSize: "0.8em", opacity: 0.65 }}>
                          {user.entryCount.toLocaleString("tr-TR")} tanım
                        </span>
                      </div>
                      {/* Karma colored green for positive, red for negative */}
                      <span
                        className="stats-rank-item__count"
                        style={{ color: user.karma >= 0 ? "var(--color-upvote, #27ae60)" : "var(--color-downvote, #e74c3c)" }}
                      >
                        {karmaDisplay} karma
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          }
        </>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const StatisticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<MainTab>("general");

  const tabs: { key: MainTab; label: string }[] = [
    { key: "general", label: "Genel" },
    { key: "authors", label: "Yazarlar" },
    { key: "rookies", label: "Çaylaklar" },
    { key: "entries", label: "Tanımlar" },
    { key: "people",  label: "Kişiler" },
    { key: "clubs",   label: "Kulüpler" },
    { key: "teams",   label: "Takımlar" },
    { key: "fans",        label: "Taraftar" },
    { key: "leaderboard", label: "Liderlik Tablosu" },
  ];

  return (
    <div className="stats-page">
      <div className="stats-page__header">
        <h1 className="stats-page__title">İstatistikler</h1>
      </div>

      <div className="stats-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`stats-tab${activeTab === tab.key ? " stats-tab--active" : ""}${tab.key === "leaderboard" ? " stats-tab--leaderboard" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "general" && <GeneralTab />}
      {activeTab === "authors" && <RoleTab role="AUTHOR" roleLabel="Yazar" />}
      {activeTab === "rookies" && <RoleTab role="ROOKIE" roleLabel="Çaylak" />}
      {activeTab === "entries" && <EntriesTab />}
      {activeTab === "people"  && <PeopleTab />}
      {activeTab === "clubs"   && <ClubsTab />}
      {activeTab === "teams"   && <TeamsTab />}
      {activeTab === "fans"        && <FansTab />}
      {activeTab === "leaderboard" && <LeaderboardTab />}
    </div>
  );
};

export { StatisticsPage };
export default StatisticsPage;